import { Trash2 } from 'lucide-react';
import { useMemo, useState, type SyntheticEvent } from 'react';
import { useTranslation } from 'react-i18next';

import { ErrorState } from '@nucleus-commerce/admin-kit/errors';
import {
  ListStonesShape,
  ListStonesStatus,
  ListStonesStoneType,
  type Stone,
} from '@nucleus-commerce/api-client/platform';
import { useFormat } from '@nucleus-commerce/i18n/react';
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@nucleus-commerce/ui/components/alert-dialog';
import { Button } from '@nucleus-commerce/ui/components/button';
import {
  Card,
  CardAction,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@nucleus-commerce/ui/components/card';
import { Input } from '@nucleus-commerce/ui/components/input';
import { Label } from '@nucleus-commerce/ui/components/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@nucleus-commerce/ui/components/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@nucleus-commerce/ui/components/table';

import { Mono } from '@/shared/ui/mono';
import { StatusDot, type StatusTone } from '@/shared/ui/status-dot';

import { useDeleteStone } from '../hooks/use-delete-stone';
import { useStones } from '../hooks/use-stones';
import { useSuppliers } from '../hooks/use-suppliers';
import { stoneListParams, type StoneFilters } from '../schemas/diamonds-search';

import { ImportStonesDialog } from './import-stones-dialog';

// Radix Select has no empty value; this stands for "no filter".
const ANY = '__any';

const STATUS_TONE: Record<Stone['status'], StatusTone> = {
  available: 'success',
  reserved: 'warning',
  sold: 'muted',
  unavailable: 'muted',
};

export interface StonesPanelProps {
  filters: StoneFilters;
  onFiltersChange: (filters: StoneFilters) => void;
  canManage: boolean;
}

interface EnumFilterProps {
  id: string;
  label: string;
  value: string | undefined;
  options: ReadonlyArray<{ value: string; label: string }>;
  anyLabel: string;
  onChange: (value: string | undefined) => void;
}

function EnumFilter({ id, label, value, options, anyLabel, onChange }: EnumFilterProps) {
  return (
    <div className="flex flex-col gap-1.5">
      <Label htmlFor={id}>{label}</Label>
      <Select
        value={value ?? ANY}
        onValueChange={(next) => onChange(next === ANY ? undefined : next)}
      >
        <SelectTrigger id={id} className="w-full">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value={ANY}>{anyLabel}</SelectItem>
          {options.map((option) => (
            <SelectItem key={option.value} value={option.value}>
              {option.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}

/** The pool newest first, filtered in the URL; certificate or stone number search with `q`. */
export function StonesPanel({ filters, onFiltersChange, canManage }: StonesPanelProps) {
  const { t } = useTranslation(['diamonds', 'common']);
  const format = useFormat();
  const params = useMemo(() => stoneListParams(filters), [filters]);
  const query = useStones(params);
  const suppliers = useSuppliers();
  const remove = useDeleteStone();
  const [deleting, setDeleting] = useState<Stone | null>(null);
  const [q, setQ] = useState(filters.q ?? '');
  const supplierNames = useMemo(
    () => new Map((suppliers.data?.items ?? []).map((s) => [s.id, s.name])),
    [suppliers.data],
  );
  const stones = query.data?.pages.flatMap((page) => page.items) ?? [];

  const set = (patch: Partial<StoneFilters>) => onFiltersChange({ ...filters, ...patch });
  const onSearch = (event: SyntheticEvent) => {
    event.preventDefault();
    set({ q: q.trim() === '' ? undefined : q.trim() });
  };

  return (
    <Card className="gap-0 overflow-hidden py-0" data-tour="platform.diamonds.stones">
      <CardHeader className="border-b px-5 py-4">
        <CardTitle>{t('diamonds:stones.title')}</CardTitle>
        <CardDescription>{t('diamonds:stones.description')}</CardDescription>
        {canManage ? (
          <CardAction data-tour="platform.diamonds.import">
            <ImportStonesDialog suppliers={suppliers.data?.items ?? []} />
          </CardAction>
        ) : null}
      </CardHeader>
      <div
        className="grid gap-3 border-b px-5 py-4 sm:grid-cols-2 lg:grid-cols-5 lg:items-end"
        data-tour="platform.diamonds.filters"
      >
        <EnumFilter
          id="stone-type"
          label={t('diamonds:stones.filters.type')}
          value={filters.stoneType}
          anyLabel={t('diamonds:stones.filters.any')}
          options={Object.values(ListStonesStoneType).map((value) => ({
            value,
            label: t(`diamonds:stoneType.${value}`),
          }))}
          onChange={(value) => set({ stoneType: value as StoneFilters['stoneType'] })}
        />
        <EnumFilter
          id="stone-shape"
          label={t('diamonds:stones.filters.shape')}
          value={filters.shape}
          anyLabel={t('diamonds:stones.filters.any')}
          options={Object.values(ListStonesShape).map((value) => ({
            value,
            label: t(`diamonds:shape.${value}`),
          }))}
          onChange={(value) => set({ shape: value as StoneFilters['shape'] })}
        />
        <EnumFilter
          id="stone-status"
          label={t('diamonds:stones.filters.status')}
          value={filters.status}
          anyLabel={t('diamonds:stones.filters.any')}
          options={Object.values(ListStonesStatus).map((value) => ({
            value,
            label: t(`diamonds:status.${value}`),
          }))}
          onChange={(value) => set({ status: value as StoneFilters['status'] })}
        />
        <EnumFilter
          id="stone-supplier"
          label={t('diamonds:stones.filters.supplier')}
          value={filters.supplierId}
          anyLabel={t('diamonds:stones.filters.any')}
          options={(suppliers.data?.items ?? []).map((s) => ({ value: s.id, label: s.name }))}
          onChange={(value) => set({ supplierId: value })}
        />
        <form onSubmit={onSearch} className="flex flex-col gap-1.5" role="search">
          <Label htmlFor="stone-q">{t('diamonds:stones.filters.q')}</Label>
          <Input
            id="stone-q"
            value={q}
            placeholder={t('diamonds:stones.filters.qPlaceholder')}
            onChange={(event) => setQ(event.target.value)}
          />
        </form>
      </div>
      {query.isError ? (
        <div className="p-5">
          <ErrorState error={query.error} onRetry={() => void query.refetch()} />
        </div>
      ) : (
        <Table className="text-[13px]" aria-label={t('diamonds:stones.title')}>
          <TableHeader>
            <TableRow className="hover:bg-transparent">
              <TableHead className="h-10 pl-5 text-xs font-medium">
                {t('diamonds:stones.columns.stone')}
              </TableHead>
              <TableHead className="h-10 text-xs font-medium">
                {t('diamonds:stones.columns.grade')}
              </TableHead>
              <TableHead className="h-10 text-xs font-medium">
                {t('diamonds:stones.columns.certificate')}
              </TableHead>
              <TableHead className="h-10 text-xs font-medium">
                {t('diamonds:stones.columns.supplier')}
              </TableHead>
              <TableHead className="h-10 text-right text-xs font-medium">
                {t('diamonds:stones.columns.cost')}
              </TableHead>
              <TableHead className="h-10 text-xs font-medium">
                {t('diamonds:stones.columns.status')}
              </TableHead>
              <TableHead className="h-10 pr-5">
                <span className="sr-only">{t('diamonds:stones.columns.actions')}</span>
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {query.isSuccess && stones.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} className="text-muted-foreground h-20 text-center">
                  {t('diamonds:stones.empty')}
                </TableCell>
              </TableRow>
            ) : (
              stones.map((stone) => (
                <TableRow key={stone.id}>
                  <TableCell className="py-2.5 pl-5">
                    <span className="flex flex-col">
                      <span className="font-medium">
                        {t('diamonds:stones.summary', {
                          carat: format.number(stone.carat, { minimumFractionDigits: 2 }),
                          shape: t(`diamonds:shape.${stone.shape}`, { defaultValue: stone.shape }),
                        })}
                      </span>
                      <span className="text-muted-foreground text-xs">
                        {t(`diamonds:stoneType.${stone.stoneType}`)} ·{' '}
                        <Mono>{stone.stoneNumber}</Mono>
                      </span>
                    </span>
                  </TableCell>
                  <TableCell className="py-2.5">
                    {[stone.color, stone.clarity, stone.cut].filter(Boolean).join(' · ')}
                  </TableCell>
                  <TableCell className="py-2.5">
                    {stone.certificateNumber ? (
                      <span>
                        {stone.lab ? `${stone.lab} ` : ''}
                        <Mono className="text-foreground">{stone.certificateNumber}</Mono>
                      </span>
                    ) : (
                      '—'
                    )}
                  </TableCell>
                  <TableCell className="py-2.5">
                    {stone.supplierId
                      ? (supplierNames.get(stone.supplierId) ?? '—')
                      : t('diamonds:stones.noSupplier')}
                  </TableCell>
                  <TableCell className="py-2.5 text-right tabular-nums">
                    {format.money(stone.cost)}
                  </TableCell>
                  <TableCell className="py-2.5">
                    <StatusDot tone={STATUS_TONE[stone.status]}>
                      {t(`diamonds:status.${stone.status}`)}
                    </StatusDot>
                  </TableCell>
                  <TableCell className="py-2.5 pr-5 text-right">
                    {canManage ? (
                      <Button
                        variant="ghost"
                        size="icon-sm"
                        className="text-destructive hover:text-destructive"
                        aria-label={t('diamonds:stones.delete', { number: stone.stoneNumber })}
                        onClick={() => setDeleting(stone)}
                      >
                        <Trash2 aria-hidden />
                      </Button>
                    ) : null}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      )}
      {query.hasNextPage ? (
        <div className="flex justify-center border-t py-3">
          <Button
            variant="outline"
            size="sm"
            disabled={query.isFetchingNextPage}
            onClick={() => void query.fetchNextPage()}
          >
            {t('diamonds:stones.loadMore')}
          </Button>
        </div>
      ) : null}
      <AlertDialog
        open={deleting !== null}
        onOpenChange={(open) => {
          if (!open) setDeleting(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {t('diamonds:stones.deleteTitle', { number: deleting?.stoneNumber ?? '' })}
            </AlertDialogTitle>
            <AlertDialogDescription>{t('diamonds:stones.deleteBody')}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={remove.isPending}>
              {t('common:actions.cancel')}
            </AlertDialogCancel>
            <Button
              variant="destructive"
              disabled={remove.isPending}
              onClick={() => {
                if (!deleting) return;
                remove.mutate(deleting.id, { onSuccess: () => setDeleting(null) });
              }}
            >
              {t('diamonds:stones.deleteConfirm')}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
}
